import React, { useEffect, useMemo, useState } from 'react';

import { getFilterDefinition } from '../../engine/filterRegistry';
import {
  type Filter,
  type FilterId,
  isSegmentedFilter,
} from '../../types/filters';
import { canFilterBeSegmented } from '../../utils/filterSegmentation';
import {
  mapSegmentOptionToFilterId,
  mapSegmentOptionToKey,
  type Segment,
} from './SegmentSelector';

export const useSegmentFlow = (
  filterToUse: Filter | null,
  externalSegmentStep?: number,
  setExternalSegmentStep?: (step: number) => void
) => {
  // Use external step state if provided, otherwise manage internally
  const [internalSegmentStep, setInternalSegmentStep] = useState<number>(0);
  const segmentStep = externalSegmentStep ?? internalSegmentStep;
  const setSegmentStep = setExternalSegmentStep ?? setInternalSegmentStep;

  const [selectedSegmentKey, setSelectedSegmentKey] = useState<string | null>(
    null
  );
  const [pendingSegmentFilter, setPendingSegmentFilter] =
    useState<Filter | null>(null);

  // Local component state for segments (takes precedence over reducer state)
  // Initialize from reducer state when editing existing filter, or empty when creating new filter
  // Use function initializer to compute initial value from filterToUse
  const getInitialSegmentations = (): Record<string, Filter> | null => {
    if (!filterToUse) return null;
    if (!canFilterBeSegmented(filterToUse)) return null;
    if (isSegmentedFilter(filterToUse)) {
      return filterToUse.segmentations || {};
    }
    return {};
  };

  const initialSegmentations = getInitialSegmentations();
  const [localSegmentations, setLocalSegmentations] = useState<Record<
    string,
    Filter
  > | null>(initialSegmentations);

  // Extract complex expressions for dependency array
  const isFilterSegmented = filterToUse
    ? isSegmentedFilter(filterToUse)
    : false;

  // Track the last filter ID we synced from to detect when filter actually changes
  const lastSyncedFilterIdRef = React.useRef<FilterId | null>(null);

  // Sync localSegmentations when filter ID changes (new filter selected), but not when segments change
  // This prevents overwriting local edits with reducer state
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (!filterToUse) {
      setLocalSegmentations(null);
      lastSyncedFilterIdRef.current = null;
      return;
    }

    // Only sync if this is a different filter (filter ID changed)
    // This prevents overwriting local edits when the same filter re-renders
    if (lastSyncedFilterIdRef.current === filterToUse.id) {
      return;
    }

    // Filter ID changed - sync from reducer
    lastSyncedFilterIdRef.current = filterToUse.id;

    if (canFilterBeSegmented(filterToUse)) {
      if (isSegmentedFilter(filterToUse)) {
        // Sync from reducer state when filter first loads
        setLocalSegmentations(filterToUse.segmentations || {});
      } else {
        // Creating new filter or filter not yet segmented: start with empty segmentations
        setLocalSegmentations({});
      }
    } else {
      // Not a segmentable filter: clear local state
      setLocalSegmentations(null);
    }
  }, [
    filterToUse?.id, // Only sync when filter ID changes, not when segments change
    filterToUse?.type,
    isFilterSegmented,
  ]);

  const handleNewSegment = () => {
    setSegmentStep(1);
  };

  const handleSegmentSelect = (segment: Segment) => {
    if (!filterToUse) return;

    const segmentFilterId = mapSegmentOptionToFilterId(segment);
    const segmentKey = mapSegmentOptionToKey(segment);

    if (!segmentFilterId || !segmentKey) return;

    // Get filter definition to create initial filter state
    const definition = getFilterDefinition(segmentFilterId);
    if (!definition) return;

    // Create initial filter from definition (empty/default state)
    const initialSegmentFilter: Filter = { ...definition };

    // Store locally in component state - NOT in reducer state yet
    setPendingSegmentFilter(initialSegmentFilter);
    setSelectedSegmentKey(segmentKey);
    // Transition to segment filter form WITHOUT adding to reducer state
    setSegmentStep(2);
  };

  const handleEditSegment = (segmentKey: string) => () => {
    setSelectedSegmentKey(segmentKey);
    setSegmentStep(2);
  };

  const handleRemoveSegment =
    (segmentKey: string) => (e: React.MouseEvent<SVGElement, MouseEvent>) => {
      e.stopPropagation();
      if (!filterToUse) return;

      // Remove from local component state
      setLocalSegmentations((prev) => {
        if (!prev || !prev[segmentKey]) {
          return prev;
        }
        const { [segmentKey]: _removed, ...rest } = prev;
        return rest;
      });
    };

  const handleSegmentBack = () => {
    if (segmentStep === 2) {
      // If editing a segment, go back to segment selector
      setSegmentStep(1);
      setSelectedSegmentKey(null);
      setPendingSegmentFilter(null);
    } else {
      // Go back to main filter form
      setSegmentStep(0);
      setSelectedSegmentKey(null);
      setPendingSegmentFilter(null);
    }
  };

  const canBeSegmented = filterToUse
    ? canFilterBeSegmented(filterToUse)
    : false;

  // Merge local component state segments with reducer state segments
  // Local state takes precedence (for unsaved changes)
  const segmentations = useMemo(() => {
    if (!canBeSegmented) return undefined;

    // If we have local state, use it directly (even if empty - user removed all segments)
    // This ensures local edits (including removals) are reflected immediately
    if (localSegmentations !== null) {
      return Object.keys(localSegmentations).length > 0
        ? localSegmentations
        : undefined;
    }

    // If no local state, use reducer state
    const reducerSegments =
      filterToUse && isSegmentedFilter(filterToUse)
        ? filterToUse.segmentations || {}
        : {};
    return Object.keys(reducerSegments).length > 0
      ? reducerSegments
      : undefined;
  }, [canBeSegmented, filterToUse, localSegmentations]);

  // Determine which filter to show in the form
  // If in step 2 (segment filter form), show the segment filter (from state or pending)
  // Otherwise, unwrap segmented filters to show the wrapped filter's form
  const getUnwrappedFilter = (): Filter | null => {
    if (!filterToUse) return null;

    // If editing a segment (step 2), get the segment filter from local state or pending
    if (segmentStep === 2 && selectedSegmentKey) {
      // Check local component state first
      if (localSegmentations?.[selectedSegmentKey]) {
        return localSegmentations[selectedSegmentKey];
      }

      // Then check reducer state
      if (isSegmentedFilter(filterToUse) && filterToUse.segmentations) {
        const reducerSegment = Object.entries(filterToUse.segmentations).find(
          ([key]) => key === selectedSegmentKey
        )?.[1];
        if (reducerSegment) {
          return reducerSegment;
        }
      }

      // Finally, use pending segment filter
      return pendingSegmentFilter;
    }

    // Otherwise, show the main filter (unwrap if segmented)
    if (isSegmentedFilter(filterToUse)) {
      return filterToUse.wrappedFilter;
    }
    return filterToUse;
  };

  return {
    segmentStep,
    selectedSegmentKey,
    pendingSegmentFilter,
    localSegmentations,
    setLocalSegmentations,
    setSelectedSegmentKey,
    setPendingSegmentFilter,
    setSegmentStep,
    handleNewSegment,
    handleSegmentSelect,
    handleEditSegment,
    handleRemoveSegment,
    handleSegmentBack,
    canBeSegmented,
    segmentations,
    getUnwrappedFilter,
  };
};
